import React, { useEffect, useState } from "react";
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { AcknowledgedMessageStrip, solidPrimaryIconButtonSx } from "./AcknowledgedMessageStrip";
import Refresh from "@mui/icons-material/Refresh";
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import CreateApi from "../Settings/appSettings";
import { useDataContext } from "../Context/useDataContext";
import { useLSContext } from "../Context/useLSContext";
import { useNavigate } from "react-router-dom";
import { LeaderboardDisplay } from "@moment-golf/leaderboard";
import type { SeriesPoints } from "@moment-golf/leaderboard";
import { usePaceOfPlay } from "../Hooks/usePaceOfPlay";
import { useMessageContext } from "../Context/useMessageContext";
import { hasActivePacePushMessage } from "@moment-golf/types";

interface LeaderboardProps {}

export const Leaderboard = ({}: LeaderboardProps) => {
    const navigate = useNavigate();
    const api = CreateApi();

    const { factor, appTheme, windowSize, isPWA, setToggleScoring } = useLSContext();

    const {
        adminMode,
        eventData,
        leaderboardData,
        leaderboardCourseData,
        currentRound,
        setLeaderboardData,
        leaderboardLastQuery,
        setLeaderboardLastQuery,
        scoreData,
        setLeaderboardCourseData,
        scoreCode,
        registrationData,
    } = useDataContext();

    const paceDisplay = usePaceOfPlay();
    const { activeMessages } = useMessageContext();
    const showPaceDisplay = scoreData.length > 0 && hasActivePacePushMessage(activeMessages);

    const [leaderboardRefreshing, setLeaderboardRefreshing] = useState(false);
    const [seriesPoints, setSeriesPoints] = useState<SeriesPoints | undefined>(undefined);
    const appBarHeightPx = windowSize.appBarHeight ?? 60;

    // Resolve series points from the event's collection whenever eventData changes.
    useEffect(() => {
        const eventGroupIDs: string[] = eventData?.eventGroupIDs ?? [];
        if (eventGroupIDs.length === 0) {
            setSeriesPoints(undefined);
            return;
        }
        api.get('/lb/GetGroupSeriesPoints', { params: { _id: eventGroupIDs[0] } })
            .then((res: any) => setSeriesPoints(res.data?.seriesPoints ?? undefined))
            .catch(() => setSeriesPoints(undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventData?._id]);

    // Tell LSContext the leaderboard tab is active (triggers scoring toggle UI)
    useEffect(() => {
        const id = setTimeout(() => setToggleScoring(true), 100);
        return () => clearTimeout(id);
    }, []);

    // Redirect non-admin scorers with no score data back to login
    useEffect(() => {
        if (scoreData.length === 0 && !adminMode) {
            navigate('/login/');
        }
    }, [scoreData, adminMode]);

    // Load (or reload) leaderboard whenever the active event is known — required after Restart App clears
    // leaderboardData but leaves a recent leaderboardLastQuery, which previously blocked the stale-only fetch
    // and left LeaderboardDisplay spinning (it only clears loading when leaderboardData has rows).
    useEffect(() => {
        if (!eventData?._id) return;
        fetchLeaderboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventData?._id]);

    // Admin polling — refetch every 60 s while admin mode is active
    useEffect(() => {
        if (!eventData || !adminMode) return;
        const interval = setInterval(fetchLeaderboardData, 60000);
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventData, adminMode]);

    // Trigger an immediate fetch if the cached data is stale (> 1 min old)
    useEffect(() => {
        if (new Date().getTime() > leaderboardLastQuery.getTime() + 60000 && eventData?._id) {
            fetchLeaderboardData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leaderboardLastQuery, eventData?._id]);

    const fetchLeaderboardData = async () => {
        const response = await api.get('/ls/GetLeaderboardData', {
            params: {
                eventID: eventData._id,
                refDate: leaderboardLastQuery,
            },
        });

        const data = response.data;

        // Delta-merge: update existing entries; fall back to full replace
        const updatedLeaderboardData =
            leaderboardData.length > 0
                ? leaderboardData.map((item: any) => {
                      const match = data.eventScores.find(
                          (newItem: any) => newItem._id === item._id
                      );
                      return match ?? item;
                  })
                : data.eventScores;

        setLeaderboardLastQuery(new Date());
        setLeaderboardData(updatedLeaderboardData);
        if (data.leaderBoardCourseData?.length > 0) {
            setLeaderboardCourseData(data.leaderBoardCourseData);
        }
    };

    // Floating action bar: 50px bar + 16px offset + scroll breathing room; safe-area added in package scroll padding
    const bottomClearance = 120;

    const handleRefresh = async () => {
        if (!eventData?._id || leaderboardRefreshing) return;
        setLeaderboardRefreshing(true);
        try {
            await fetchLeaderboardData();
        } finally {
            setLeaderboardRefreshing(false);
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                position: 'fixed',
                top: 0,
                left: 0,
                overscrollBehavior: 'none',
                backgroundColor: 'sepia.background',
            }}
            className="live-scoring"
        >
            <LeaderboardDisplay
                bottomPadding={bottomClearance}
                hideCourseName
                headerSlot={
                    <Box sx={{ width: '100%', marginTop: `${appBarHeightPx}px`, pt: '10px' }}>
                        <AcknowledgedMessageStrip
                            leadingContent={
                                showPaceDisplay ? (
                                    <Tooltip
                                        title={paceDisplay.hasPace ? 'Pace of play' : 'Pace clock not yet started'}
                                        placement="bottom-start"
                                    >
                                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 0.5 }}>
                                            <TimerOutlinedIcon
                                                sx={{
                                                    fontSize: 14,
                                                    flexShrink: 0,
                                                    color: paceDisplay.hasPace
                                                        ? (paceDisplay.isOOP ? 'error.main' : 'primary.main')
                                                        : 'text.disabled',
                                                }}
                                            />
                                            {paceDisplay.hasPace && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: 12,
                                                        color: paceDisplay.isOOP ? 'error.main' : 'primary.main',
                                                        whiteSpace: 'nowrap',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {paceDisplay.elapsedFormatted}
                                                    {paceDisplay.projectedFormatted !== '—' && (
                                                        <Box component="span" sx={{ fontWeight: 400, color: paceDisplay.isOOP ? 'error.light' : 'text.secondary' }}>
                                                            {' → '}{paceDisplay.projectedFormatted}
                                                        </Box>
                                                    )}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </Tooltip>
                                ) : undefined
                            }
                            trailingAction={
                                <IconButton
                                    size="small"
                                    aria-label="Refresh leaderboard"
                                    disabled={leaderboardRefreshing || !eventData?._id}
                                    onClick={() => void handleRefresh()}
                                    sx={solidPrimaryIconButtonSx}
                                >
                                    {leaderboardRefreshing ? (
                                        <CircularProgress color="inherit" size={16} />
                                    ) : (
                                        <Refresh sx={{ fontSize: 18 }} />
                                    )}
                                </IconButton>
                            }
                        />
                    </Box>
                }
                leaderboardData={leaderboardData}
                courseData={leaderboardCourseData}
                eventData={eventData}
                currentRound={currentRound}
                scoreCode={scoreCode}
                registrationData={registrationData}
                factor={factor}
                appTheme={appTheme}
                windowSize={windowSize}
                isPWA={isPWA}
                seriesPoints={seriesPoints}
            />
        </Box>
    );
};
